import { useEffect, useMemo, useState } from "react";
import Layout from "../../../Layout/Layout";
import "./OrganizationStructure.css";
import Itemservice from "../../../services/human_resource_appCommonService";
const COMPANY_NAME = "VENSAP";
const GROUP_LABELS = {
  D05: "HR Manager",
  D04: "Project Manager",
  D03: "Team Lead",
  D02: "Senior Software Developer",
  D01: "Software Developer",
  C07: "Office Boy",
  C05: "Driver",
};

const GROUP_ICON = {
  D05: "bi-people-fill",
  D04: "bi-person-workspace",
  D03: "bi-person-badge",
  D02: "bi-person-gear",
  D01: "bi-person",
  C07: "bi-person",
  C05: "bi-truck",
};
const DEFAULT_ICON = "bi-person";

const levelRank = (code) => {
  const m = /^L(\d+)$/i.exec(code || "");
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
};

const displayName = (emp) =>
  emp.employeeName
    ? `${emp.employeeName} (${emp.employeeCode})`
    : emp.employeeCode;
const OrganizationStructure = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await Itemservice.getEmployeeOrg();
      const json = res.data && res.status === undefined ? res.data : res;
      if (json.status !== 200)
        throw new Error(json.message || "Failed to load employees");
      setEmployees(json.data || []);
    } catch (err) {
      console.error("Org structure fetch error:", err);
      setError("Unable to load organization structure");
    } finally {
      setLoading(false);
    }
  };

  const tree = useMemo(() => buildOrgTree(employees), [employees]);
  return (
    <div className="org-container">
      <Layout />

      <div className="org-header">
        <h2>
          <i className="bi bi-diagram-3-fill"></i>
          Organization Structure
        </h2>
        <p>Company hierarchy overview</p>
      </div>

      {loading && <p className="org-status">Loading organization chart...</p>}
      {!loading && error && <p className="org-status org-error">{error}</p>}

      {!loading && !error && (
        <div className="org-chart-scroll">
          {employees.length === 0 ? (
            <p className="org-status">No employees found.</p>
          ) : (
            <ul className="tree">
              <li>
                <div className="tree-badge tree-badge-company">
                  {COMPANY_NAME}
                </div>
                <ul>
                  <TreeNode node={tree} isRoot />
                </ul>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const TreeNode = ({ node, isRoot }) => {
  return (
    <li>
      {node.type === "employee" ? (
        <EmployeeBox emp={node.emp} isRoot={isRoot} />
      ) : (
        <div
          className={`tree-badge ${node.type === "department" ? "tree-badge-dept" : "tree-badge-role"}`}
        >
          {node.label} <span className="org-level-count">({node.count})</span>
        </div>
      )}

      {node.children && node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <TreeNode node={child} key={child.key} />
          ))}
        </ul>
      )}
    </li>
  );
};
const EmployeeBox = ({ emp, isRoot }) => (
  <div className={`org-card ${isRoot ? "ceo-card" : "manager-card"}`}>
    <div className="org-icon">
      <i
        className={`bi ${GROUP_ICON[emp.designationCode] || DEFAULT_ICON}`}
      ></i>
    </div>
    <div>
      <h4>{displayName(emp)}</h4>
      <span>
        {emp.designation || GROUP_LABELS[emp.designationCode] || "Unspecified"}
      </span>
    </div>
  </div>
);
function buildOrgTree(employees) {
  if (!employees || employees.length === 0) return null;

  let topRank = Infinity;
  employees.forEach((e) => {
    const r = levelRank(e.organizationLevelCode);
    if (r < topRank) topRank = r;
  });
  const topEmployees = employees.filter(
    (e) => levelRank(e.organizationLevelCode) === topRank,
  );

  let root = null;
  let rest = employees;
  if (topEmployees.length === 1) {
    root = topEmployees[0];
    rest = employees.filter((e) => e.employeeCode !== root.employeeCode);
  }

  const deptMap = {};
  rest.forEach((emp) => {
    const deptCode = emp.departmentCode || "UNSPEC";
    const deptName = emp.department || "Unspecified Department";
    if (!deptMap[deptCode])
      deptMap[deptCode] = {
        code: deptCode,
        name: deptName,
        roleMap: {},
        count: 0,
      };
    const dept = deptMap[deptCode];
    dept.count += 1;

    const roleCode = emp.designationCode || emp.jobRoleCode || "UNSPEC_ROLE";
    const roleLabel =
      GROUP_LABELS[roleCode] || emp.designation || emp.jobRole || "Unspecified";

    if (!dept.roleMap[roleCode]) {
      dept.roleMap[roleCode] = {
        code: roleCode,
        label: roleLabel,
        rank: levelRank(emp.organizationLevelCode),
        employees: [],
      };
    }
    dept.roleMap[roleCode].employees.push(emp);
    dept.roleMap[roleCode].rank = Math.min(
      dept.roleMap[roleCode].rank,
      levelRank(emp.organizationLevelCode),
    );
  });

  const deptNodes = Object.values(deptMap)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((dept) => {
      const roleNodes = Object.values(dept.roleMap)
        .sort((a, b) => a.rank - b.rank)
        .map((role) => ({
          type: "role",
          key: `${dept.code}-${role.code}`,
          label: role.label,
          count: role.employees.length,
          children: role.employees.map((emp) => ({
            type: "employee",
            key: emp.employeeCode,
            emp,
          })),
        }));

      return {
        type: "department",
        key: dept.code,
        label: dept.name,
        count: dept.count,
        children: roleNodes,
      };
    });

  if (!root) {
    return {
      type: "department",
      key: "COMPANY",
      label: "Company",
      count: employees.length,
      children: deptNodes,
    };
  }

  return {
    type: "employee",
    key: root.employeeCode,
    emp: root,
    children: deptNodes,
  };
}

export default OrganizationStructure;
